export interface DocumentLink {
  filename: string;
  document_type: string;
  view_url: string;
  download_url: string;
  uploaded_at: string;
}

export interface DownloadProgress {
  current: number;
  total: number;
  fileName: string;
  status: 'downloading' | 'processing' | 'complete' | 'error';
  percentage: number;
}

export interface DownloadResult {
  name: string;
  blob: Blob;
  success: boolean;
  error?: string;
}

export interface DownloadOptions {
  maxRetries?: number;
  retryDelay?: number;
  timeout?: number;
  concurrent?: boolean;
  maxConcurrent?: number;
}

export type ProgressCallback = (progress: DownloadProgress) => void;

const DEFAULT_OPTIONS: Required<DownloadOptions> = {
  maxRetries: 3,
  retryDelay: 1000,
  timeout: 30000,
  concurrent: false,
  maxConcurrent: 3,
};

/**
 * Downloads a single file with retry logic and timeout
 * 
 * @param {string} url - The URL to download from
 * @param {string} fileName - The name of the file being downloaded
 * @param {Partial<DownloadOptions>} options - Download configuration options
 * 
 * @returns {Promise<Blob>} Promise that resolves to the downloaded file as a Blob
 * 
 * @throws {Error} When all retry attempts fail or network errors occur
 */
const downloadFile = async (
  url: string,
  fileName: string,
  options: Partial<DownloadOptions> = {}
): Promise<Blob> => {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  let lastError: Error | null = null;

  for (let attempt = 1; attempt <= opts.maxRetries; attempt++) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), opts.timeout);

      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: {
          Accept: '*/*',
          'Cache-Control': 'no-cache',
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const blob = await response.blob();

      // Validate blob
      if (blob.size === 0) {
        throw new Error('Downloaded file is empty');
      }

      return blob;
    } catch (error: any) {
      lastError = error;

      if (attempt < opts.maxRetries) {
        console.warn(
          `Download attempt ${attempt} failed for ${fileName}, retrying in ${opts.retryDelay}ms...`
        );
        await delay(opts.retryDelay);
      }
    }
  }

  throw new Error(
    `Failed to download ${fileName} after ${opts.maxRetries} attempts: ${lastError?.message}`
  );
};

/**
 * Downloads multiple files with progress tracking and error handling
 * 
 * @param {DocumentLink[]} documents - Array of documents to download
 * @param {ProgressCallback} onProgress - Optional callback for progress updates
 * @param {Partial<DownloadOptions>} options - Download configuration options
 * 
 * @returns {Promise<DownloadResult[]>} Promise that resolves to array of download results
 */
const downloadFiles = async (
  documents: DocumentLink[],
  onProgress?: ProgressCallback,
  options: Partial<DownloadOptions> = {}
): Promise<DownloadResult[]> => {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  if (opts.concurrent) {
    return downloadFilesConcurrently(documents, opts, onProgress);
  } else {
    return downloadFilesSequentially(documents, opts, onProgress);
  }
};

/**
 * Downloads files sequentially (one at a time) to avoid overwhelming the server
 * 
 * @param {DocumentLink[]} documents - Array of documents to download
 * @param {Required<DownloadOptions>} options - Complete download configuration
 * @param {ProgressCallback} onProgress - Optional callback for progress updates
 * 
 * @returns {Promise<DownloadResult[]>} Promise that resolves to array of download results
 */
const downloadFilesSequentially = async (
  documents: DocumentLink[],
  options: Required<DownloadOptions>,
  onProgress?: ProgressCallback
): Promise<DownloadResult[]> => {
  const results: DownloadResult[] = [];

  for (let i = 0; i < documents.length; i++) {
    const doc = documents[i];
    const current = i + 1;
    const percentage = Math.round((current / documents.length) * 100);

    if (onProgress) {
      onProgress({
        current,
        total: documents.length,
        fileName: doc.filename,
        status: 'downloading',
        percentage,
      });
    }

    try {
      const blob = await downloadFile(doc.download_url, doc.filename, options);
      results.push({
        name: sanitizeFileName(doc.filename),
        blob,
        success: true,
      });
    } catch (error: any) {
      console.error(`Failed to download ${doc.filename}:`, error);
      results.push({
        name: sanitizeFileName(doc.filename),
        blob: new Blob(),
        success: false,
        error: error.message,
      });
    }
  }

  return results;
};

/**
 * Downloads files concurrently with rate limiting to improve performance
 * 
 * @param {DocumentLink[]} documents - Array of documents to download
 * @param {Required<DownloadOptions>} options - Complete download configuration
 * @param {ProgressCallback} onProgress - Optional callback for progress updates
 * 
 * @returns {Promise<DownloadResult[]>} Promise that resolves to array of download results
 */
const downloadFilesConcurrently = async (
  documents: DocumentLink[],
  options: Required<DownloadOptions>,
  onProgress?: ProgressCallback
): Promise<DownloadResult[]> => {
  const results: DownloadResult[] = [];
  let completed = 0;

  const downloadPromises = documents.map(async (doc, index) => {
    try {
      const blob = await downloadFile(doc.download_url, doc.filename, options);
      completed++;

      if (onProgress) {
        onProgress({
          current: completed,
          total: documents.length,
          fileName: doc.filename,
          status: 'downloading',
          percentage: Math.round((completed / documents.length) * 100),
        });
      }

      return {
        name: sanitizeFileName(doc.filename),
        blob,
        success: true,
      };
    } catch (error: any) {
      completed++;
      console.error(`Failed to download ${doc.filename}:`, error);

      if (onProgress) {
        onProgress({
          current: completed,
          total: documents.length,
          fileName: doc.filename,
          status: 'error',
          percentage: Math.round((completed / documents.length) * 100),
        });
      }

      return {
        name: sanitizeFileName(doc.filename),
        blob: new Blob(),
        success: false,
        error: error.message,
      };
    }
  });

  // Process downloads with concurrency limit
  const chunks = chunkArray(downloadPromises, options.maxConcurrent);

  for (const chunk of chunks) {
    const chunkResults = await Promise.all(chunk);
    results.push(...chunkResults);
  }

  return results;
};

/**
 * Creates a zip file from multiple blobs with compression
 * 
 * @param {DownloadResult[]} files - Array of download results to include in zip
 * @param {ProgressCallback} onProgress - Optional callback for progress updates
 * 
 * @returns {Promise<Blob>} Promise that resolves to the zip file as a Blob
 * 
 * @throws {Error} When JSZip fails to create the archive
 */
const createZipFile = async (
  files: DownloadResult[],
  onProgress?: ProgressCallback
): Promise<Blob> => {
  if (onProgress) {
    onProgress({
      current: 1,
      total: 1,
      fileName: 'Creating zip file...',
      status: 'processing',
      percentage: 0,
    });
  }

  const JSZip = (await import('jszip')).default;
  const zip = new JSZip();

  // Add successful downloads to zip
  const successfulFiles = files.filter(f => f.success);
  const failedFiles = files.filter(f => !f.success);

  successfulFiles.forEach(({ name, blob }) => {
    zip.file(name, blob);
  });

  // Add error report if there were failures
  if (failedFiles.length > 0) {
    console.log(failedFiles);
  }

  const zipBlob = await zip.generateAsync({
    type: 'blob',
    compression: 'DEFLATE',
    compressionOptions: { level: 6 },
  });

  if (onProgress) {
    onProgress({
      current: 1,
      total: 1,
      fileName: 'Zip file created',
      status: 'complete',
      percentage: 100,
    });
  }

  return zipBlob;
};

/**
 * Triggers a file download in the browser by creating a temporary download link
 * 
 * @param {Blob} blob - The file content as a Blob
 * @param {string} filename - The desired filename for the download
 * 
 * @returns {void}
 */
const triggerDownload = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  link.style.display = 'none';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up the URL object
  setTimeout(() => URL.revokeObjectURL(url), 100);
};

/**
 * Main function to download all documents, zip them, and trigger download
 * 
 * @param {DocumentLink[]} documents - Array of documents to download
 * @param {string} baseFileName - Base name for the zip file (will be sanitized)
 * @param {ProgressCallback} onProgress - Optional callback for progress updates
 * @param {Partial<DownloadOptions>} options - Download configuration options
 * 
 * @returns {Promise<{successCount: number, failureCount: number, zipSize: number}>} Download summary statistics
 * 
 * @throws {Error} When no documents are provided, no valid documents found, or all downloads fail
 * 
 * @example
 * ```typescript
 * const documents = [
 *   { filename: 'resume.pdf', download_url: 'https://...', ... },
 *   { filename: 'certificate.jpg', download_url: 'https://...', ... }
 * ];
 * 
 * const result = await downloadAllDocuments(
 *   documents,
 *   'John_Doe',
 *   (progress) => console.log(`${progress.percentage}% - ${progress.fileName}`),
 *   { concurrent: true, maxRetries: 5 }
 * );
 * 
 * console.log(`Downloaded ${result.successCount} files, ${result.failureCount} failed`);
 * ```
 */
export const downloadAllDocuments = async (
  documents: DocumentLink[],
  baseFileName: string,
  onProgress?: ProgressCallback,
  options: Partial<DownloadOptions> = {}
): Promise<{ successCount: number; failureCount: number; zipSize: number }> => {
  if (!documents || documents.length === 0) {
    throw new Error('No documents provided for download');
  }

  const validDocuments = documents.filter(doc => doc.download_url && doc.filename);

  if (validDocuments.length === 0) {
    throw new Error('No valid documents found for download');
  }

  try {
    // Download all files
    const downloadResults = await downloadFiles(validDocuments, onProgress, options);

    const successCount = downloadResults.filter(r => r.success).length;
    const failureCount = downloadResults.filter(r => !r.success).length;

    if (successCount === 0) {
      throw new Error('No files were successfully downloaded');
    }

    // Create zip file
    const zipBlob = await createZipFile(downloadResults, onProgress);

    // Generate filename and trigger download
    const timestamp = new Date().toISOString().split('T')[0];
    const sanitizedBaseName = sanitizeFileName(baseFileName);
    const filename = `${sanitizedBaseName}_documents_${timestamp}.zip`;

    triggerDownload(zipBlob, filename);

    return {
      successCount,
      failureCount,
      zipSize: zipBlob.size,
    };
  } catch (error) {
    console.error('Error in downloadAllDocuments:', error);
    throw error;
  }
};

// Utility functions

/**
 * Creates a delay promise for implementing retry logic
 * 
 * @param {number} ms - Number of milliseconds to delay
 * 
 * @returns {Promise<void>} Promise that resolves after the specified delay
 */
const delay = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

/**
 * Sanitizes filename for safe file system usage by removing invalid characters
 * 
 * @param {string} fileName - The original filename to sanitize
 * @returns {string} Sanitized filename safe for file system use
 * 
 * @example
 * ```typescript
 * sanitizeFileName('My Document: Version 2.0.pdf')
 * // Returns: 'My_Document_Version_2.0.pdf'
 * ```
 */
const sanitizeFileName = (fileName: string): string => {
  return fileName
    .replace(/[<>:"/\\|?*]/g, '_')
    .replace(/\s+/g, '_')
    .replace(/_{2,}/g, '_')
    .trim();
};

/**
 * Splits an array into smaller chunks of specified size for batch processing
 * 
 * @param {T[]} array - The array to split into chunks
 * @param {number} size - Maximum size of each chunk
 * 
 * @returns {T[][]} Array of chunks, each containing up to 'size' elements
 * 
 * @example
 * ```typescript
 * chunkArray([1, 2, 3, 4, 5], 2)
 * // Returns: [[1, 2], [3, 4], [5]]
 * ```
 */
const chunkArray = <T>(array: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
};
